import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAuthToken, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Droplets } from "lucide-react";

/**
 * Handles the Emergent Google OAuth callback.
 *
 * Flow:
 *  - User clicked "Continue with Google" → got redirected to Emergent's
 *    OAuth page → Emergent redirects them BACK to `{origin}/auth-callback`
 *    with `#session_id=…` in the URL fragment.
 *  - This page reads the session_id, POSTs it to the backend, and lets the
 *    backend exchange it for an app JWT (set in an httpOnly cookie). We then
 *    refresh the AuthContext and route the user to their role's home.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. The redirect_url passed to Emergent is built from
 * `window.location.origin` at click-time inside Login.jsx.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setError("Missing session_id — please try signing in again.");
      return;
    }

    const run = async () => {
      try {
        const { data } = await api.post("/auth/google-callback", { session_id: sessionId });
        if (data?.token) setAuthToken(data.token);
        // Strip the hash so refresh/back doesn't re-trigger.
        window.history.replaceState(null, "", window.location.pathname);
        // Hydrate AuthContext (will also re-issue /auth/me with the new cookie).
        await refresh();
        const role = data?.user?.role;
        const home = (role === "admin" || role === "secretary") ? "/admin"
                   : role === "driver" ? "/driver"
                   : "/portal";
        toast.success(`Welcome${data?.user?.name ? `, ${data.user.name}` : ""}!`);
        navigate(home, { replace: true });
      } catch (e) {
        setError(formatErr(e.response?.data?.detail) || e.message || "Google sign-in failed.");
      }
    };
    run();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="text-center max-w-sm" data-testid="auth-callback-screen">
        <Droplets className="w-8 h-8 mx-auto text-primary mb-3" />
        {error ? (
          <>
            <p className="overline text-destructive mb-2">Sign-in failed</p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <button
              type="button"
              className="text-sm underline text-primary"
              onClick={() => navigate("/login", { replace: true })}
              data-testid="auth-callback-back-to-login"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <p className="overline mb-1">Finishing sign-in</p>
            <p className="text-sm text-muted-foreground">Linking your Google account…</p>
          </>
        )}
      </div>
    </div>
  );
}
